// Environment configuration
// Uses EXPO_PUBLIC_* vars (bundled into web builds) with fallbacks

import Constants from 'expo-constants';
import { Platform } from 'react-native';

const isAndroidEmulator = Platform.OS === 'android' && !Constants.isDevice;

const resolveDevUrl = (url: string): string => {
  if (!isAndroidEmulator) return url;
  try {
    const parsed = new URL(url);
    // 10.0.2.2 is the Android emulator's alias for the host loopback, so ONLY
    // loopback hosts may be rewritten. A routable LAN IP (e.g. the Metro host
    // 10.0.1.15) is reachable as-is and must never be remapped -- doing so
    // sends the request to a dead address, which blackholes on a real device.
    const isLoopback =
      parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    if (isLoopback) {
      parsed.hostname = '10.0.2.2';
      return parsed.toString().replace(/\/$/, '');
    }
  } catch {}
  return url;
};

// Derive the LAN host the device used to reach the Metro packager, so dev
// API/auth URLs auto-track the current network instead of pointing at
// localhost (which, on a physical device, is the phone itself).
const getDevHost = (): string | null => {
  const raw =
    Constants.expoConfig?.hostUri ??
    Constants.expoGoConfig?.debuggerHost ??
    (Constants as any).manifest2?.extra?.expoClient?.hostUri;

  if (!raw) return null;

  const host = raw.split('/')[0].split(':')[0];
  if (!host || host === 'localhost' || host === '127.0.0.1') return null;

  return host;
};

const getEnvironment = (): 'development' | 'preview' | 'production' | 'showcase' => {
  const env = process.env.EXPO_PUBLIC_APP_ENV;
  if (env === 'production' || env === 'preview' || env === 'development' || env === 'showcase') {
    return env;
  }
  return 'development';
};

const getApiUrl = (): string => {
  // EXPO_PUBLIC_ vars are bundled at build time
  if (process.env.EXPO_PUBLIC_API_URL) {
    return resolveDevUrl(process.env.EXPO_PUBLIC_API_URL);
  }

  // Fallback based on environment
  const env = getEnvironment();
  if (env === 'production') return 'https://api.flavrly.app';
  if (env === 'preview') return 'https://api-preview.flavrly.app';
  if (env === 'showcase') return 'https://api-demo.flavrly.app';

  // Development: derive from the Metro packager host so URLs track the LAN.
  const devHost = getDevHost();
  if (devHost) return resolveDevUrl(`http://${devHost}:8787`);
  return resolveDevUrl('http://localhost:8787');
};

const getAuthUrl = (): string => {
  // EXPO_PUBLIC_ vars are bundled at build time
  if (process.env.EXPO_PUBLIC_AUTH_URL) {
    return resolveDevUrl(process.env.EXPO_PUBLIC_AUTH_URL);
  }

  // Fallback based on environment
  const env = getEnvironment();
  if (env === 'production') return 'https://auth.flavrly.app';
  if (env === 'preview') return 'https://auth-preview.flavrly.app';
  if (env === 'showcase') return 'https://auth-demo.flavrly.app';

  // Development: derive from the Metro packager host so URLs track the LAN.
  const devHost = getDevHost();
  if (devHost) return resolveDevUrl(`http://${devHost}:8788`);
  return resolveDevUrl('http://localhost:8788');
};

const getUploadUrl = (): string => {
  const env = getEnvironment();
  if (env === 'production') return 'https://upload.flavrly.app';
  if (env === 'preview') return 'https://upload-preview.flavrly.app';
  if (env === 'showcase') return 'https://upload-demo.flavrly.app';
  return '';
};

const API_URL = getApiUrl();
const APP_ENV = getEnvironment();

// Cloudflare Image Transformations (/cdn-cgi/image/) are enabled only on the
// production upload zone. Widen this when other zones are confirmed.
const IMAGE_TRANSFORMS_ENABLED = APP_ENV === 'production';

export const ENV = {
  APP_ENV,
  API_URL,
  AUTH_URL: getAuthUrl(),
  UPLOAD_URL: getUploadUrl(),
  WS_URL: API_URL.replace(/^http/, 'ws'),
  IMAGE_TRANSFORMS_ENABLED,
} as const;

